import { PencilIcon } from '@heroicons/react/24/outline';
import React from 'react';

import {
  DiscriminatedTreatmentRequestUpdatedEvent,
  DiscriminatedTreatmentRequestUpdatedEventV1,
} from '@/services/api/generated/matterChangesService';
import { useGetMedicalProvidersQuery } from '@/services/api/treatmentService';

import {
  ActivityActions,
  ActivityBody,
  ActivityDescription,
  ActivityDiffList,
  ActivityIcon,
  ActivityItem,
  ActivityLink,
  CollapsibleList,
} from '../activity-feed-v2';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';

export interface TreatmentRequestUpdatedEventProps {
  event:
    | DiscriminatedTreatmentRequestUpdatedEvent
    | DiscriminatedTreatmentRequestUpdatedEventV1;
}

interface TreatmentRequestDetailField {
  label: string;
  value: string | number | boolean | null | undefined;
}

export const TreatmentRequestUpdatedEvent: React.FC<
  TreatmentRequestUpdatedEventProps
> = ({ event }) => {
  const user = getUserFromEvent(event);
  const { data: providers } = useGetMedicalProvidersQuery(event.matter_id);

  // V2 has diff, V1 doesn't
  const { diff } = event.payload;
  const treatmentRequest =
    'treatment_request' in event.payload
      ? event.payload.treatment_request
      : undefined;

  const provider = providers?.find(
    (p) => p.id === treatmentRequest?.provider_id,
  );

  const icon = (
    <ActivityIcon
      icon={
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-100">
          <PencilIcon className="h-4 w-4 text-teal-600" />
        </div>
      }
    />
  );

  const treatmentRequestDetailFields: TreatmentRequestDetailField[] = [
    { label: 'Provider', value: provider?.name },
  ];

  const detailItems = treatmentRequestDetailFields
    .filter(
      ({ value }) => value !== null && value !== undefined && value !== '',
    )
    .map(({ label, value }) => `${label}: ${value}`);

  const body = (
    <ActivityBody>
      <ActivityDescription>Records request updated</ActivityDescription>
      {diff && <ActivityDiffList diff={diff} defaultExpanded={false} />}
      {detailItems.length > 0 && (
        <CollapsibleList
          items={detailItems}
          showLabel="Show Details"
          hideLabel="Hide Details"
        />
      )}
    </ActivityBody>
  );

  const actions = treatmentRequest?.provider_id && (
    <ActivityActions>
      <ActivityLink
        href={`/matters/${event.matter_id}/treatment/${treatmentRequest.provider_id}`}
      >
        View Provider
      </ActivityLink>
    </ActivityActions>
  );

  return (
    <ActivityItem
      icon={icon}
      title="Records Request Updated"
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
      actions={actions}
    />
  );
};
